import S from "sanctuary";
import { createSelector } from "reselect";
import * as Nested from "@/util/Nested";
import * as AlertBarState from "@/components/alert-bar/AlertBar.state";
import { LINKED_TRANSACTION_STATUSES } from "@/sanctuary/typedefs";
import {
  getUserTimezone,
  getCurrentUserRole,
} from "@/components/currentUser/CurrentUser.state";
import {
  getPayment,
  getFetchPaymentRequestIsPending,
  getPartialRefundContainerOpen,
  getPartialRefundItems,
  getRefundPaymentRequestIsPending,
  getRefundPaymentRequestError,
  getVoidPaymentRequestIsPending,
  getVoidPaymentRequestError,
  getVoidRefundModalOpen,
  getRefundWindows,
  getReceiptDownloadPending,
} from "./Payment.selectors";
import { getServiceTypeDescription } from "@/components/currentUser/Agencies.state";
import { getDescriptionKeys, getSubdescriptionKeys } from "@/state/client.state";

const INITIAL_STATE = {
  payment: S.Nothing,
  fetchRequestPending: false,
  fetchRequestError: S.Nothing,
  voidRequestPending: false,
  voidRequestError: S.Nothing,
  refundRequestPending: false,
  refundRequestError: S.Nothing,
  paymentModificationPreAuthError: S.Nothing,
  paymentModificationPreAuthRequestPending: false,
  partialRefundContainerOpen: false,
  partialRefundItems: [],
  voidRefundModalOpen: false,
  refundWindows: new Set(),
  receiptDownloadPending: false,
};

export const COMPONENT_NAMESPACE = "payment";

export const PAYMENT_INITIATOR_SCHEDULE_SERVICE = "schedule_service";

export const PAYMENT_MODIFICATION_TYPES = {
  VOID: "VOID",
  REFUND: "REFUND",
};

export const REQUEST_PAYMENT_MODIFICATION = "payment/REQUEST_PAYMENT_MODIFICATION";
export const requestPaymentModification =
  (modificationType) =>
  ({ accessToken }) => ({
    type: REQUEST_PAYMENT_MODIFICATION,
    payload: {
      modificationType,
      accessToken,
    },
  });

export const OPEN_PAYMENT = "payment/OPEN_PAYMENT";
export const openPayment = ({ transactionId }) => ({
  type: OPEN_PAYMENT,
  payload: { transactionId },
});

export const requestVoidPayment = requestPaymentModification(
  PAYMENT_MODIFICATION_TYPES.VOID
);
export const requestRefundPayment = requestPaymentModification(
  PAYMENT_MODIFICATION_TYPES.REFUND
);

export const REQUEST_GET_PAYMENT = "payment/REQUEST_GET_PAYMENT";
export const requestGetPayment = () => ({ type: REQUEST_GET_PAYMENT });

export const GET_PAYMENT_STARTED = "payment/GET_PAYMENT_STARTED";
export const getPaymentStarted = () => ({
  type: GET_PAYMENT_STARTED,
});

export const GET_PAYMENT_SUCCEEDED = "payment/GET_PAYMENT_SUCCEEDED";
export const getPaymentSucceeded = ({ payment }) => ({
  type: GET_PAYMENT_SUCCEEDED,
  payload: {
    payment,
  },
});

export const GET_PAYMENT_FAILED = "payment/GET_PAYMENT_FAILED";
export const getPaymentFailed = (error) => ({
  type: GET_PAYMENT_FAILED,
  payload: error,
});

export const VOID_PAYMENT_STARTED = "payment/VOID_PAYMENT_STARTED";
export const voidPaymentStarted = () => ({
  type: VOID_PAYMENT_STARTED,
});

export const VOID_PAYMENT_SUCCEEDED = "payment/VOID_PAYMENT_SUCCEEDED";
export const voidPaymentSucceeded = ({ payment }) => ({
  type: VOID_PAYMENT_SUCCEEDED,
  payload: {
    payment,
  },
});

export const VOID_PAYMENT_FAILED = "payment/VOID_PAYMENT_FAILED";
export const voidPaymentFailed = ({ error }) => ({
  type: VOID_PAYMENT_FAILED,
  error: true,
  payload: {
    error,
  },
});

export const REFUND_PAYMENT_STARTED = "payment/REFUND_PAYMENT_STARTED";
export const refundPaymentStarted = () => ({
  type: REFUND_PAYMENT_STARTED,
});

export const REFUND_PAYMENT_SUCCEEDED = "payment/REFUND_PAYMENT_SUCCEEDED";
export const refundPaymentSucceeded = ({ payment }) => ({
  type: REFUND_PAYMENT_SUCCEEDED,
  payload: {
    payment,
  },
});

export const REFUND_PAYMENT_FAILED = "payment/REFUND_PAYMENT_FAILED";
export const refundPaymentFailed = ({ error }) => ({
  type: REFUND_PAYMENT_FAILED,
  error: true,
  payload: {
    error,
  },
});

export const REQUEST_DOWNLOAD_RECEIPT = "payment/REQUEST_DOWNLOAD_RECEIPT";
export const requestDownloadReceipt = ({ transactionId }) => ({
  type: REQUEST_DOWNLOAD_RECEIPT,
  payload: { transactionId },
});

export const DOWNLOAD_RECEIPT_STARTED = "payment/DOWNLOAD_RECEIPT_STARTED";
export const downloadReceiptStarted = () => ({ type: DOWNLOAD_RECEIPT_STARTED });

export const DOWNLOAD_RECEIPT_FINISHED = "payment/DOWNLOAD_RECEIPT_FINISHED";
export const downloadReceiptFinished = () => ({ type: DOWNLOAD_RECEIPT_FINISHED });

export const CLEAR_VOID_ERROR = "payment/CLEAR_VOID_ERROR";
export const clearVoidError = () => ({
  type: CLEAR_VOID_ERROR,
});

export const CLEAR_REFUND_ERROR = "payment/CLEAR_REFUND_ERROR";
export const clearRefundError = () => ({
  type: CLEAR_REFUND_ERROR,
});

export const PAYMENT_MODIFICATION_PRE_AUTH_STARTED =
  "payment/PAYMENT_MODIFICATION_PRE_AUTH_STARTED";
export const paymentModificationPreAuthStarted = () => ({
  type: PAYMENT_MODIFICATION_PRE_AUTH_STARTED,
});

export const PAYMENT_MODIFICATION_PRE_AUTH_SUCCEEDED =
  "payment/PAYMENT_MODIFICATION_PRE_AUTH_SUCCEEDED";
export const paymentModificationPreAuthSucceeded = ({
  token,
  paymentId,
  partialRefundItems,
  modificationType,
}) => ({
  type: PAYMENT_MODIFICATION_PRE_AUTH_SUCCEEDED,
  payload: {
    token,
    paymentId,
    partialRefundItems,
    modificationType,
  },
});

export const PAYMENT_MODIFICATION_PRE_AUTH_FAILED =
  "payment/PAYMENT_MODIFICATION_PRE_AUTH_FAILED";
export const paymentModificationPreAuthFailed = ({ error }) => ({
  type: PAYMENT_MODIFICATION_PRE_AUTH_FAILED,
  error: true,
  payload: error,
});

export const CLEAR_MODIFICATION_PRE_AUTH_ERROR =
  "payment/CLEAR_MODIFICATION_PRE_AUTH_ERROR";
export const clearModificationPreAuthError = () => ({
  type: CLEAR_MODIFICATION_PRE_AUTH_ERROR,
});

export const OPEN_PARTIAL_REFUND_CONTAINER = "payment/OPEN_PARTIAL_REFUND_CONTAINER";
export const openPartialRefundContainer = () => ({
  type: OPEN_PARTIAL_REFUND_CONTAINER,
});

export const CLOSE_PARTIAL_REFUND_CONTAINER =
  "payment/CLOSE_PARTIAL_REFUND_CONTAINER";
export const closePartialRefundContainer = () => ({
  type: CLOSE_PARTIAL_REFUND_CONTAINER,
});

export const SET_PARTIAL_REFUND_LINE_ITEM = "payment/SET_PARTIAL_REFUND_LINE_ITEM";
export const setPartialRefundLineItem = ({ id, amount }) => ({
  type: SET_PARTIAL_REFUND_LINE_ITEM,
  payload: { id, amount },
});

export const TOGGLE_REFUND_ALL_LINE_ITEMS = "payment/TOGGLE_REFUND_ALL_LINE_ITEMS";
export const toggleRefundAllLineItems = ({ isSet, lineItems }) => ({
  type: TOGGLE_REFUND_ALL_LINE_ITEMS,
  payload: { isSet, lineItems },
});

export const OPEN_VOID_REFUND_MODAL = "payment/OPEN_VOID_REFUND_MODAL";
export const openVoidRefundModal = () => ({
  type: OPEN_VOID_REFUND_MODAL,
});

export const CLOSE_VOID_REFUND_MODAL = "payment/CLOSE_VOID_REFUND_MODAL";
export const closeVoidRefundModal = () => ({
  type: CLOSE_VOID_REFUND_MODAL,
});

export const SUBMIT_VOID_REFUND_MODAL = "payment/SUBMIT_VOID_REFUND_MODAL";
export const submitVoidRefundModal = (paymentType) => ({
  type: SUBMIT_VOID_REFUND_MODAL,
  payload: {
    paymentType,
  },
});

export const SET_REFUND_WINDOW = "payment/SET_REFUND_WINDOW";
export const toggleRefundWindow = (id) => ({
  type: SET_REFUND_WINDOW,
  payload: id,
});

export const _reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case GET_PAYMENT_STARTED:
      return {
        ...state,
        payment: S.Nothing,
        fetchRequestPending: true,
      };
    case GET_PAYMENT_SUCCEEDED:
      return {
        ...state,
        payment: S.Just(payload.payment),
        fetchRequestPending: false,
        fetchRequestError: S.Nothing,
      };
    case GET_PAYMENT_FAILED:
      return {
        ...state,
        fetchRequestPending: false,
        fetchRequestError: S.Just(payload),
      };
    case VOID_PAYMENT_STARTED:
      return {
        ...state,
        voidRequestPending: true,
      };
    case VOID_PAYMENT_SUCCEEDED:
      return {
        ...state,
        payment: S.Just(payload.payment),
        voidRequestPending: false,
        voidRequestError: S.Nothing,
        voidRefundModalOpen: false,
      };
    case VOID_PAYMENT_FAILED:
      return {
        ...state,
        voidRequestPending: false,
        voidRequestError: S.Just(payload),
      };
    case DOWNLOAD_RECEIPT_STARTED:
      return {
        ...state,
        receiptDownloadPending: true,
      };
    case DOWNLOAD_RECEIPT_FINISHED:
      return {
        ...state,
        receiptDownloadPending: false,
      };
    case REFUND_PAYMENT_STARTED:
      return {
        ...state,
        refundRequestPending: true,
      };
    case REFUND_PAYMENT_SUCCEEDED:
      return {
        ...state,
        payment: S.Just(payload.payment),
        refundRequestPending: false,
        refundRequestError: S.Nothing,
        partialRefundContainerOpen: false,
        voidRefundModalOpen: false,
      };
    case REFUND_PAYMENT_FAILED:
      return {
        ...state,
        refundRequestPending: false,
        refundRequestError: S.Just(payload),
      };
    case PAYMENT_MODIFICATION_PRE_AUTH_STARTED:
      return {
        ...state,
        paymentModificationPreAuthRequestPending: true,
      };
    case PAYMENT_MODIFICATION_PRE_AUTH_SUCCEEDED:
      return {
        ...state,
        paymentModificationPreAuthRequestPending: false,
        paymentModificationPreAuthError: S.Nothing,
      };
    case PAYMENT_MODIFICATION_PRE_AUTH_FAILED:
      return {
        ...state,
        paymentModificationPreAuthError: S.Just(payload),
        paymentModificationPreAuthRequestPending: false,
      };
    case CLEAR_VOID_ERROR:
      return {
        ...state,
        voidRequestError: S.Nothing,
      };
    case CLEAR_REFUND_ERROR:
      return {
        ...state,
        refundRequestError: S.Nothing,
      };
    case CLEAR_MODIFICATION_PRE_AUTH_ERROR:
      return {
        ...state,
        paymentModificationPreAuthError: S.Nothing,
      };
    case OPEN_PAYMENT:
      return {
        ...state,
        partialRefundItems: [],
        partialRefundContainerOpen: false,
      };
    case OPEN_PARTIAL_REFUND_CONTAINER:
      return {
        ...state,
        partialRefundContainerOpen: true,
      };
    case CLOSE_PARTIAL_REFUND_CONTAINER:
      return {
        ...state,
        partialRefundItems: [],
        partialRefundContainerOpen: false,
      };
    case SET_PARTIAL_REFUND_LINE_ITEM:
      return {
        ...state,
        partialRefundItems: (() => {
          const { id, amount } = payload;

          return amount == null
            ? state.partialRefundItems.filter((v) => v.id !== id)
            : [
                { id, amount },
                ...state.partialRefundItems.filter((v) => v.id !== id),
              ];
        })(),
      };
    case TOGGLE_REFUND_ALL_LINE_ITEMS:
      return {
        ...state,
        partialRefundItems: (() => {
          const { isSet, lineItems } = payload;

          const lineItemToAmount = (lineItem) => {
            const existingAmount = state.partialRefundItems.find(
              ({ id }) => id === lineItem.id.value
            )?.amount;

            const amount =
              existingAmount ?? lineItem.amountRefundableInCents.value / 100;

            return {
              id: lineItem.id.value,
              amount,
            };
          };

          return isSet ? lineItems.map(lineItemToAmount) : [];
        })(),
      };
    case OPEN_VOID_REFUND_MODAL:
      return {
        ...state,
        voidRefundModalOpen: true,
      };
    case CLOSE_VOID_REFUND_MODAL:
      return {
        ...state,
        voidRefundModalOpen: false,
      };
    case SET_REFUND_WINDOW:
      return {
        ...state,
        refundWindows: (() => {
          const refundID = payload.refundID;
          const openRefundWindows = new Set(state.refundWindows);

          if (openRefundWindows.has(refundID)) {
            openRefundWindows.delete(refundID);
          } else {
            openRefundWindows.add(refundID);
          }

          return openRefundWindows;
        })(),
      };
    default:
      return state;
  }
};

const getState = (state) => state.payment;
const getTransaction = (state) => getState(state).payment;
export const getLinkedTransactions = createSelector(
  getTransaction,
  S.pipe([
    S.map(S.prop("associatedPayments")),
    S.fromMaybe([]),
    S.filter((transaction) =>
      S.elem(S.prop("status")(transaction))(
        S.map(S.Just)(LINKED_TRANSACTION_STATUSES)
      )
    ),
  ])
);

export const _mapStateToProps = (state) => ({
  payment: S.fromMaybe({})(getPayment(state)),
  paymentIsLoaded: S.isJust(getPayment(state)),
  fetchRequestPending: getFetchPaymentRequestIsPending(state),
  userTimezone: getUserTimezone(state),
  getServiceTypeText: getServiceTypeDescription(state),
  linkedTransactions: getLinkedTransactions(state),
  getDescriptionKeys: getDescriptionKeys(state),
  getSubdescriptionKeys: getSubdescriptionKeys(state),
  refundRequestPending: getRefundPaymentRequestIsPending(state),
  refundRequestError: getRefundPaymentRequestError(state),
  partialRefundContainerOpen: getPartialRefundContainerOpen(state),
  partialRefundItems: getPartialRefundItems(state),
  voidRequestPending: getVoidPaymentRequestIsPending(state),
  voidRequestError: getVoidPaymentRequestError(state),
  voidRefundModalOpen: getVoidRefundModalOpen(state),
  refundWindows: getRefundWindows(state),
  receiptDownloadPending: getReceiptDownloadPending(state),
  userRole: getCurrentUserRole(state),
});

export const _mapDispatchToProps = (dispatch) => ({
  goToPayment: ({ transactionId }) => dispatch(openPayment({ transactionId })),
  openPartialRefundContainer: () => dispatch(openPartialRefundContainer()),
  openVoidRefundModal: () => dispatch(openVoidRefundModal()),
  closePartialRefundContainer: () => dispatch(closePartialRefundContainer()),
  closeVoidRefundModal: () => dispatch(closeVoidRefundModal()),
  submitVoidRefundModal: (paymentType) =>
    dispatch(submitVoidRefundModal(paymentType)),
  setPartialRefundLineItem: ({ id, amount }) =>
    dispatch(setPartialRefundLineItem({ id, amount })),
  toggleRefundAllLineItems: ({ isSet, lineItems }) =>
    dispatch(toggleRefundAllLineItems({ isSet, lineItems })),
  toggleRefundWindow: (id) => dispatch(toggleRefundWindow(id)),
  downloadReceipt: ({ transactionId }) =>
    dispatch(requestDownloadReceipt({ transactionId })),
});

export const PAYMENT_PAGE_ALERT_BAR_ACTION = "payment/PAYMENT_PAGE_ALERT_BAR_ACTION";
export const paymentPageAlertBarAction = Nested.nestedAction(
  PAYMENT_PAGE_ALERT_BAR_ACTION
);

const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    forms: {},
    alertBars: {
      paymentPageAlertBar: {
        ...AlertBarState,
        actionType: PAYMENT_PAGE_ALERT_BAR_ACTION,
      },
    },
  },
  "payment"
);

const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
